import { useState, useEffect, useCallback } from 'react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Megaphone,
  FolderOpen,
  TrendingUp,
  BarChart3,
  Wallet,
  LifeBuoy,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useTier } from '@/hooks/useTier';
import { useUnseenSupportReplies } from '@/hooks/useSupportTickets';
import { ThemeToggle } from './ThemeToggle';

const SIDEBAR_COLLAPSED_KEY = 'mustauto:partner-sidebar-collapsed';

type TierFeature = 'marketIntelligence' | 'opportunities' | 'pdfExport';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/campaigns', label: 'Campanhas', icon: Megaphone },
  { to: '/catalog', label: 'Catálogo', icon: FolderOpen },
  { to: '/balance', label: 'Saldo', icon: Wallet },
  { to: '/market-intelligence', label: 'Inteligência', icon: BarChart3, tierGate: 'marketIntelligence' as TierFeature },
  { to: '/opportunities', label: 'Oportunidades', icon: TrendingUp, tierGate: 'opportunities' as TierFeature },
  { to: '/support', label: 'Suporte', icon: LifeBuoy },
  { to: '/settings', label: 'Configurações', icon: Settings },
] as const;

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === '[') {
        toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { collapsed, toggle };
}

export function Sidebar() {
  const { user, signOut } = useAuth();
  const matchRoute = useMatchRoute();
  const { collapsed, toggle } = useSidebarCollapsed();
  const { canAccess, tierKnown } = useTier();
  // Parceiro não recebe push nem sino: o badge é o único aviso de resposta.
  const unseenSupportReplies = useUnseenSupportReplies();

  return (
    <aside
      className={cn(
        'flex h-screen flex-col bg-sidebar text-sidebar-foreground transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex items-center border-b border-white/10 px-3 py-5">
        {collapsed ? (
          <div className="flex w-full flex-col items-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-lg font-bold">
              M
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-3 px-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-lg font-bold">
              M
            </div>
            <div>
              <div className="text-sm font-bold tracking-wide">MustAuto</div>
              <div className="text-xs text-sidebar-foreground/60">Portal Parceiro</div>
            </div>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <div className="flex justify-end px-2 py-1">
        <button
          onClick={toggle}
          className="rounded-md p-1.5 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
          title={collapsed ? 'Expandir sidebar ([)' : 'Recolher sidebar ([)'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 px-3 py-2">
        {navItems.map((item) => {
          const { to, label, icon: Icon } = item;
          const tierGate = 'tierGate' in item ? item.tierGate : undefined;
          // O cadeado so aparece quando o tier e CONHECIDO. Enquanto o
          // dashboard nao responde (ou falhou), `canAccess` devolve false por
          // seguranca — mas desenhar o cadeado ali afirma ao parceiro Ouro que
          // ele nao tem o que pagou. Sem o cadeado o item segue navegavel, e a
          // propria pagina diz o que houve.
          const locked = tierGate && tierKnown ? !canAccess(tierGate) : false;
          const active = !!matchRoute({ to });
          const badge = to === '/support' ? unseenSupportReplies : 0;
          return (
            <Link
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              className={cn(
                'relative flex items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
                active
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-active/50 hover:text-white',
              )}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && label}
              {!collapsed && locked && <Lock size={14} className="ml-auto shrink-0 opacity-50" />}
              {!collapsed && badge > 0 && (
                <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {badge}
                </span>
              )}
              {collapsed && badge > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User */}
      <div className="border-t border-white/10 p-4 space-y-3">
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-bold"
              title={user?.name ?? 'Parceiro'}
            >
              {(user?.name ?? 'P')[0].toUpperCase()}
            </div>
            <button
              onClick={signOut}
              className="rounded-md p-2 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="truncate">
              <div className="text-sm font-medium truncate">{user?.name ?? 'Parceiro'}</div>
              <div className="text-xs text-sidebar-foreground/50">Parceiro</div>
            </div>
            <button
              onClick={signOut}
              className="rounded-md p-2 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
        <ThemeToggle />
      </div>
    </aside>
  );
}
